import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import CurrencyPoundOutlinedIcon from "@mui/icons-material/CurrencyPoundOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EuroSymbolIcon from "@mui/icons-material/EuroSymbol";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import BrandOverviewTableSkeleton from "../../../BrandSkeletons/BrandOverviewTableSkeleton";
import {
  ColumnDefinition,
  ColumnFormat,
  formatToUTC,
  formatValueToDecimal,
  resolveEffectiveCurrencyFormat,
} from "../../../utilitiesOfBrandConfig/helperOfBrandConfig";

interface Props {
  reportType: "daily_user" | "daily_brand" | "monthly_brand";
  // ColumnDefinition[] replaces the old Record<string,string> headings — carries
  // label, format, and sort metadata already ordered correctly from the parent.
  columnDefinitions: ColumnDefinition[];
}

// ─── Columns that should be summed in the Total row, per report type ─────────
// Labels are matched as produced by getColumnLabel() in helperOfBrandConfig.
const TOTAL_COLUMN_LABELS: Record<Props["reportType"], Set<string>> = {
  daily_user: new Set([
    "Registrations",
    "FTD",
    "QFTD",
    "Net Revenue",
    "Revshare Income",
    "CPA Income",
    "Total Income",
    "CPA Est Dollar",
    "Revshare Est Dollar",
    "Total Income Est Dollar",
    "Deposit Amount",
  ]),
  daily_brand: new Set([
    "Clicks",
    "Registrations",
    "FTD",
    "QFTD",
    "Net Revenue",
    "Revshare Income",
    "CPA Income",
    "Total Income",
    "CPA Est Dollar",
    "Revshare Est Dollar",
    "Total Income Est Dollar",
    "Deposit Amount",
  ]),
  monthly_brand: new Set([
    "Registrations",
    "FTD",
    "QFTD",
    "Net Revenue",
    "Revshare Income",
    "CPA Income",
    "Total Income",
    "CPA Est Dollar",
    "Revshare Est Dollar",
    "Total Income Est Dollar",
    "Deposit Amount",
  ]),
};

const PerformanceTable = ({ reportType, columnDefinitions }: Props) => {
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "", direction: "desc" });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loaderForSwitchingTabs = useSelector(
    (state: any) => state.brandConfigurationReport.loaderForSwitchingTabs,
  );

  const overviewPerformanceData = useSelector(
    (state: any) =>
      state.brandConfigurationReport.overviewPerformanceData[reportType],
  );

  const loaderForDateRangeSelection = useSelector(
    (state: any) => state.brandConfigurationReport.loaderForDateRangeSelection,
  );

  const showSkeleton = loaderForSwitchingTabs || loaderForDateRangeSelection;

  // ─── Reset sort to first column whenever tab changes or columns load ─────
  const prevReportTypeRef = React.useRef<string>(reportType);

  React.useEffect(() => {
    if (columnDefinitions?.length > 0) {
      const isTabSwitch = prevReportTypeRef.current !== reportType;
      const isSortMissing = !sort.keytoSort;

      if (isTabSwitch || isSortMissing) {
        prevReportTypeRef.current = reportType;
        setSort({ keytoSort: columnDefinitions[0]?.key, direction: "desc" });
      }
    }
    // eslint-disable-next-line
  }, [reportType, columnDefinitions]);

  // ─── Cell renderer — driven by ColumnFormat from API metadata ────────────────
  const renderCell = (col: ColumnDefinition, row: any) => {
    const val = row[col.key];
    const effectiveFormat = resolveEffectiveCurrencyFormat(
      col.key,
      col.format,
      row,
    );

    switch (effectiveFormat) {
      case ColumnFormat.DATE:
      case ColumnFormat.DATETIME:
        return val ? formatToUTC(val) : "";

      case ColumnFormat.CURRENCY_GBP:
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {val != null ? formatValueToDecimal(val) : 0}
            <CurrencyPoundOutlinedIcon
              sx={{ fontSize: "1.1rem", ml: 1, color: "#0000008F" }}
            />
          </Box>
        );

      case ColumnFormat.CURRENCY_USD:
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {val != null ? formatValueToDecimal(val) : 0}
            <AttachMoneyIcon
              sx={{ fontSize: "1.4rem", ml: 1, color: "#0000008F" }}
            />
          </Box>
        );

      case ColumnFormat.CURRENCY_EUR:
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {val != null ? formatValueToDecimal(val) : 0}
            <EuroSymbolIcon
              sx={{ fontSize: "1.1rem", ml: 1, color: "#0000008F" }}
            />
          </Box>
        );

      case ColumnFormat.CURRENCY_MISSING:
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {val != null ? formatValueToDecimal(val) : 0}
            <Typography
              variant="caption"
              sx={{ color: "warning.main", whiteSpace: "nowrap" }}
            >
              Currency is missing
            </Typography>
          </Box>
        );

      case ColumnFormat.FLOAT:
        return val != null ? formatValueToDecimal(val) : 0;

      case ColumnFormat.INT: {
        // null/missing means "no value provided" — keep the cell empty rather
        // than showing a misleading 0. API sometimes sends counts as
        // decimal-looking strings (e.g. "0.0") — round those to a whole number.
        if (val == null || val === "") return "";
        const num = parseFloat(val);
        return isNaN(num) ? val : Math.round(num);
      }

      case ColumnFormat.BOOLEAN:
        return val ? "Yes" : "No";

      case ColumnFormat.STRING:
      default:
        return val ?? "";
    }
  };

  // ─── Sorting ──────────────────────────────────────────────────────────────────
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) return [...arrayToSort];

      return arrayToSort.slice().sort((a, b) => {
        const key = sort.keytoSort;
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === undefined || aValue === "") return 1;
        if (bValue === undefined || bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        }
        return sort.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    },
    [sort],
  );

  const sortedData = useMemo(() => {
    if (!overviewPerformanceData?.length) return [];
    return getSortedData(overviewPerformanceData);
  }, [overviewPerformanceData, getSortedData]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  // ─── Totals — computed from the full dataset, independent of pagination ─────
  // Alongside the sum, tracks the actual resolved currency (via
  // resolveEffectiveCurrencyFormat, same as each row uses) so the Total row's
  // icon matches what the rows themselves display — not a static per-column
  // default. When rows disagree, the most common resolved currency wins; when
  // every row's currency is missing, no icon is shown at all.
  const columnTotals = useMemo(() => {
    const totalLabels = TOTAL_COLUMN_LABELS[reportType];
    const totals: Record<
      string,
      { sum: number; hasValue: boolean; currencyFormat: ColumnFormat | null }
    > = {};

    columnDefinitions.forEach((col) => {
      if (!totalLabels?.has(col.label)) return;
      let sum = 0;
      let hasValue = false;
      const currencyCounts = new Map<ColumnFormat, number>();

      (overviewPerformanceData ?? []).forEach((row: any) => {
        const raw = row?.[col.key];
        if (raw != null && raw !== "") {
          const num = parseFloat(raw);
          if (!isNaN(num)) {
            sum += num;
            hasValue = true;
          }
        }

        const effectiveFormat = resolveEffectiveCurrencyFormat(
          col.key,
          col.format,
          row,
        );
        if (effectiveFormat !== ColumnFormat.CURRENCY_MISSING) {
          currencyCounts.set(
            effectiveFormat,
            (currencyCounts.get(effectiveFormat) ?? 0) + 1,
          );
        }
      });

      let currencyFormat: ColumnFormat | null = null;
      let maxCount = 0;
      currencyCounts.forEach((count, format) => {
        if (count > maxCount) {
          maxCount = count;
          currencyFormat = format;
        }
      });

      totals[col.key] = { sum, hasValue, currencyFormat };
    });

    return totals;
  }, [overviewPerformanceData, columnDefinitions, reportType]);

  // ─── Total cell renderer — shows the dominant resolved currency icon ────────
  const renderTotalCell = (col: ColumnDefinition) => {
    const entry = columnTotals[col.key];
    // No row provided an actual value (all null/empty) — leave the total blank
    // rather than showing a misleading 0.
    if (!entry || !entry.hasValue) return "";

    let formatted: string;
    if (col.format === ColumnFormat.INT) {
      // Registrations / Clicks are always whole-number counts.
      formatted = String(Math.round(entry.sum));
    } else if (col.format === ColumnFormat.STRING) {
      // FTD / QFTD carry whatever precision the API sent (2, 2.3, 0, ...) —
      // round only to guard against floating-point noise, no fixed decimals.
      formatted = String(Math.round(entry.sum * 100) / 100);
    } else {
      formatted = formatValueToDecimal(entry.sum);
    }

    if (!entry.currencyFormat) return formatted;

    switch (entry.currencyFormat) {
      case ColumnFormat.CURRENCY_GBP:
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {formatted}
            <CurrencyPoundOutlinedIcon
              sx={{ fontSize: "1.1rem", ml: 1, color: "#0000008F" }}
            />
          </Box>
        );

      case ColumnFormat.CURRENCY_USD:
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {formatted}
            <AttachMoneyIcon
              sx={{ fontSize: "1.4rem", ml: 1, color: "#0000008F" }}
            />
          </Box>
        );

      case ColumnFormat.CURRENCY_EUR:
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {formatted}
            <EuroSymbolIcon
              sx={{ fontSize: "1.1rem", ml: 1, color: "#0000008F" }}
            />
          </Box>
        );

      default:
        return formatted;
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  React.useEffect(() => {
    setPage(0);
  }, [reportType]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleHeaderClick = (col: ColumnDefinition) => {
    if (!col.sortable) return;
    setSort({
      keytoSort: col.key,
      direction:
        col.key === sort.keytoSort && sort.direction === "asc" ? "desc" : "asc",
    });
  };

  return (
    <>
      {showSkeleton ? (
        <BrandOverviewTableSkeleton />
      ) : (
        <>
          {sortedData.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 3,
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No records found.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                className="performanceTable"
                sx={{
                  padding: "0px 0px",
                  boxShadow: "none",
                  borderStartStartRadius: "0px",
                  overflowX: "auto",
                }}
              >
                <Table aria-label="collapsible table" size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columnDefinitions.map((col) => (
                        <TableCell key={col.key} align="left">
                          <TableSortLabel
                            active={sort.keytoSort === col.key}
                            direction={
                              sort.keytoSort === col.key
                                ? sort.direction
                                : "desc"
                            }
                            onClick={() => handleHeaderClick(col)}
                            disabled={!col.sortable}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.map((row: any, rowIndex: number) => (
                      <React.Fragment key={row.id ?? rowIndex}>
                        <TableRow
                          sx={{
                            "& > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          {columnDefinitions.map((col) => (
                            <TableCell key={col.key} align="left">
                              {renderCell(col, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    ))}
                    <TableRow
                      sx={{
                        backgroundColor: "#F7F7F8",
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      <TableCell
                        colSpan={Math.min(2, columnDefinitions.length)}
                        align="center"
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Total
                        </Typography>
                      </TableCell>
                      {columnDefinitions.slice(2).map((col) => (
                        <TableCell key={col.key} align="left">
                          {renderTotalCell(col)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {/* Left Section */}
                  <Box sx={{ justifySelf: "start", display: "flex" }}>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 100]}
                      component="div"
                      count={sortedData.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      classes={{ displayedRows: "hidden-displayed-rows" }}
                      sx={{
                        ".MuiTablePagination-actions": { display: "none" },
                        ".MuiTablePagination-input": { marginRight: "15px" },
                      }}
                    />

                    <TablePagination
                      rowsPerPageOptions={[]}
                      component="div"
                      count={sortedData.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        ".MuiTablePagination-selectLabel,.MuiTablePagination-select,.MuiTablePagination-actions":
                          { display: "none" },
                      }}
                    />
                  </Box>

                  {/* Center Section */}
                  <Box sx={{ justifySelf: "center" }}>
                    <Pagination
                      count={Math.ceil(sortedData.length / rowsPerPage)}
                      page={page + 1}
                      onChange={(_, value) => handleChangePage(null, value - 1)}
                      showFirstButton
                      showLastButton
                      color="secondary"
                      size="small"
                    />
                  </Box>

                  {/* Empty right section to balance layout */}
                  <Box />
                </Box>
              </Stack>
            </>
          )}
        </>
      )}
    </>
  );
};

export default PerformanceTable;
